
import React, {useState, useEffect, useMemo, useRef} from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    ChartOptions,
    ChartData,
    TooltipItem,
    Plugin
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import moment from 'moment';
import { getTimeSeriesData } from '../../services/api.service';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend, TimeScale, zoomPlugin);

interface TimeSeriesDataPoint {
    timestamp: string;
    value: number | null;
}

interface TimeSeriesData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        variable: string;
        start_date: string;
        end_date: string;
        count: number;
        aggregation: string;
        total_count: number;
    };
}

interface VoltageTimeSeriesChartProps {
    stationId: string;
    startDate: string;
    endDate: string;
    height?: number;
    title?: string;
}

const VoltageTimeSeriesChart: React.FC<VoltageTimeSeriesChartProps> = ({
    stationId,
    startDate,
    endDate,
    height = 400,
    title = 'Voltage vs 100% Battery Comparison'
}) => {
    // State management
    const [voltageData, setVoltageData] = useState<TimeSeriesData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [chartKey, setChartKey] = useState<number>(0);

    const chartRef = useRef<any>(null);

    // Reset zoom function
    const resetZoom = () => {
        if (chartRef.current) {
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetZoom) {
                chartInstance.resetZoom();
            } else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
                chartInstance.chart.resetZoom();
            }
        }
    };

    const adjustedStartDate = useMemo(() =>
        moment(startDate).subtract(3, 'month').format('YYYY-MM-DDTHH:mm:ss'),
        [startDate]
    );

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch voltage data using 'lv' variable (battery voltage)
                const voltageResponse = await getTimeSeriesData(stationId, 'lv', adjustedStartDate, endDate);
                setVoltageData(voltageResponse);
            } catch (err) {
                setError('Failed to fetch voltage data');
                console.error('Error fetching voltage data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [stationId, adjustedStartDate, endDate]);

    // Chart data configuration
    const chartData: ChartData<'line'> = useMemo(() => {
        const datasets: any[] = [];

        // Voltage dataset (time series)
        if (voltageData?.data && voltageData.data.length > 0) {
            datasets.push({
                label: 'Battery Voltage (V)',
                data: voltageData.data.map(d => ({
                    x: moment(d.timestamp).valueOf(),
                    y: d.value // Keep null values as null for gaps
                })),
                borderColor: 'rgb(54, 162, 235)',
                backgroundColor: 'rgba(255, 99, 132, 0.1)',
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4,
                spanGaps: false, // Show gaps where data is missing
                yAxisID: 'y'
            });

            // 100% Battery reference line (straight horizontal line)
            const firstTimestamp = moment(voltageData.data[0].timestamp).valueOf();
            const lastTimestamp = moment(voltageData.data[voltageData.data.length - 1].timestamp).valueOf();

            datasets.push({
                label: '100% Battery Reference',
                data: [
                    { x: firstTimestamp, y: 100 },
                    { x: lastTimestamp, y: 100 }
                ],
                borderColor: 'rgb(220, 53, 69)', // Green color
                backgroundColor: 'transparent',
                borderWidth: 2,
                borderDash: [10, 5], // Dashed line pattern
                pointRadius: 0, // No points on the line
                pointHoverRadius: 0,
                tension: 0, // Straight line
                yAxisID: 'y1' // Use right y-axis for percentage
            });
        }

        return { datasets };
    }, [voltageData]);

    // Chart options configuration
    const chartOptions: ChartOptions<'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            title: {
                display: true,
                text: title,
                font: {
                    size: 16
                }
            },
            legend: {
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    title: (context: TooltipItem<'line'>[]) => {
                        if (context.length > 0) {
                            return moment(context[0].parsed.x).format('YYYY-MM-DD HH:mm');
                        }
                        return '';
                    },
                    label: (context: TooltipItem<'line'>) => {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y;
                        if (label.includes('Voltage')) {
                            return `${label}: ${value?.toFixed(2)}V`;
                        } else if (label.includes('100%')) {
                            return `${label}: ${value}%`;
                        }
                        return `${label}: ${value}`;
                    }
                }
            },
            // Zoom plugin configuration
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        hour: 'DD/MM',
                        day: 'DD/MM',
                        week: 'DD/MM',
                        month: 'MM'
                    },
                    tooltipFormat: 'DD MMM YYYY HH:mm'
                },
                title: {
                    display: true,
                    text: 'Date'
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Voltage (V)'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: false,
                },
                min: 90,
                max: 110, // Give some space above 100%
                grid: {
                    drawOnChartArea: false, // Don't draw grid lines for right axis
                },
                ticks: {
                      stepSize: 20, // This creates potential ticks at 90, 100, 110
                    callback: function(value) {
                        // Only show "100%" for the value 100, hide everything else
                        if (value === 100) {
                            return '100%';
                        }
                        return ''; // Return empty string to hide all other tick labels
                    },
                    color: '#dc3545', // Red color to match the reference line
                    font: {
                        size: 12,
                        weight: 'bold'
                    }

                }
            }
        },
    }), [title]);

    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
                        <div className="text-muted">
                            <i className="fa fa-spinner fa-spin fa-3x mb-3"></i>
                            <p>Loading voltage comparison data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }

    // No data state
    if (!voltageData?.data || voltageData.data.length === 0) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="text-center py-5">
                        <i className="fa fa-line-chart fa-3x text-muted mb-3"></i>
                        <p className="text-muted">No voltage data available for the selected time range</p>
                    </div>
                </div>
            </div>
        );
    }

    // Main render
    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="card-title mb-0">{title}</h5>
                        <small className="text-muted">
                            Station: {stationId} | {moment(startDate).format('YYYY-MM-DD')} to {moment(endDate).format('YYYY-MM-DD')}
                        </small>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        {/* Reset zoom button */}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetZoom}
                            title="Reset Zoom"
                        >
                            <i className="fa fa-search-minus me-1"></i>
                            Reset Zoom
                        </button>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div style={{ height: `${height}px` }}>
                    <Chart
                        key={chartKey}
                        ref={chartRef}
                        type="line"
                        data={chartData}
                        options={chartOptions}
                    />
                </div>

                {/* Metadata display */}
                <div className="mt-2 d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                        {voltageData?.metadata?.aggregation
                            ? `Data aggregation: ${voltageData.metadata.aggregation}`
                            : 'Raw measurements (no aggregation)'
                        }
                    </small>
                    <small className="text-muted">
                        Data points: {voltageData?.metadata?.count || 0} / {voltageData?.metadata?.total_count || 0}
                    </small>
                </div>
            </div>
        </div>
    );
};

export default VoltageTimeSeriesChart;